<template>
  <div class="display-mode">
    <div class="dm-section">
      <p class="dm-section__title">Carpets</p>
      <p class="dm-section__description">You can add multiple carpets to an event.</p>
      <div
        v-for="(carpet, index) in eventCarpets"
        :key="JSON.stringify(carpet)"
        class="carpet-item"
      >
        <v-text-field
          :value="carpet.name"
          autocomplete="off"
          background-color="#fff"
          class="carpet__field"
          label="Title"
          outlined
          hide-details
          @input="handleUpdateCarpetName(index, carpet.name)"
        />
        <v-btn
          class="carpet__remove-btn"
          color="red"
          :disabled="isUpdatingCarpets"
          icon
          text
          @click="handleRemoveCarpet(index)"
        >
          <v-icon>{{ mdiMinusCircleOutline }}</v-icon>
        </v-btn>
      </div>
      <v-btn
        class="carpet__add-btn pa-0"
        color="var(--color-blue-800)"
        :disabled="isUpdatingCarpets"
        text
        tile
        @click="handleAddCarpet"
      >
        Add carpet
        <v-icon>{{ mdiPlus }}</v-icon>
      </v-btn>
    </div>

    <div class="dm-section">
      <p class="dm-section__title">Display Mode Logo</p>
      <p class="dm-section__description">
        You can add a logo to the display mode.
        <br />
        The recommended aspect ratio is 1:1. For example, 800x800px.
        <br />
        Recommended format is PNG or WEBP with transparent background.
      </p>
      <v-img
        :src="srcLogo"
        class="dm-logo"
      >
        <v-btn
          v-if="!srcLogo"
          class="btn-upload"
          color="var(--color-white)"
          outlined
          tile
          @click="openLogoDialog"
        >
          Upload Logo
          <v-icon class="ml-4">
            {{ mdiUpload }}
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          class="btn-edit white--text"
          color="var(--color-blue-800)"
          tile
          @click="openLogoDialog"
        >
          Edit Logo
          <v-icon class="ml-4">
            {{ mdiLeadPencil }}
          </v-icon>
        </v-btn>
        <!--      eslint-disable vuejs-accessibility/form-control-has-label-->
        <input
          ref="inputLogoRef"
          accept="image/png, image/jpeg, image/webp"
          name="DMLogo"
          style="display: none"
          type="file"
          @change="handlerLogoChange"
        />
        <!--      eslint-enable vuejs-accessibility/form-control-has-label-->
      </v-img>
    </div>

    <div class="dm-section">
      <p class="dm-section__title">Display Mode Background Image</p>
      <p class="dm-section__description">
        You can add a background image to the display mode.
        <br />
        The image will be cropped to fit the display mode.
        <br />
        The recommended aspect ratio is 16:9. For example, 1920x1080px.
      </p>
      <v-img
        :src="srcScoreboardBgImage"
        class="dm-bg-image"
      >
        <v-btn
          v-if="!srcScoreboardBgImage"
          class="btn-upload"
          color="var(--color-white)"
          outlined
          tile
          @click="openBgImageDialog"
        >
          Upload image
          <v-icon class="ml-4">
            {{ mdiUpload }}
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          class="btn-edit white--text"
          color="var(--color-blue-800)"
          tile
          @click="openBgImageDialog"
        >
          Edit Image
          <v-icon class="ml-4">
            {{ mdiLeadPencil }}
          </v-icon>
        </v-btn>
        <!--      eslint-disable vuejs-accessibility/form-control-has-label-->
        <input
          ref="inputBgImageRef"
          accept="image/png, image/jpeg"
          name="DMBgImage"
          style="display: none"
          type="file"
          @change="handlerBgImageChange"
        />
        <!--      eslint-enable vuejs-accessibility/form-control-has-label-->
      </v-img>
    </div>

    <div class="display-mode__navigation navigation">
      <div class="navigation__wrap-dots mt-6">
        <span
          v-for="i in 6"
          v-show="isNew"
          :key="i"
          :class="i === currentDotNav ? 'navigation__dot--active' : ''"
          class="navigation__dot"
        />
      </div>
      <div
        v-show="error"
        class="navigation__text-error error--text text-center"
      >
        {{ error }}
      </div>
      <div class="navigation__wrap-btn mb-10">
        <div class="d-flex flex-column flex-sm-row-reverse">
          <v-btn
            :disabled="!isInfoFilledOut || isUpdatingCarpets"
            :loading="loadingNextBtn"
            class="navigation__btn"
            color="var(--color-blue-800)"
            height="48"
            outlined
            tile
            @click="handlerClickNext"
          >
            {{ backStep ? 'save' : 'next' }}
          </v-btn>
          <v-btn
            :disabled="isUpdatingCarpets"
            class="navigation__btn"
            color="var(--color-blue-800)"
            height="48"
            outlined
            tile
            @click="backStep ? handlerClickCancelEdit() : handlerClickBack()"
          >
            {{ backStep ? 'cancel' : 'back' }}
          </v-btn>
        </div>
        <v-btn
          v-if="!backStep"
          block
          class="navigation__btn-cancel"
          color="var(--color-blue-800)"
          :disabled="isUpdatingCarpets"
          height="48"
          max-width="194"
          text
          @click="store.dispatch('toggleCancelEventCreation')"
        >
          cancel
        </v-btn>
        <CreateEventCancel
          :event="eventData"
          @saveAsDraft="saveAsDraft"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CreateEventCancel from '@/pages/CreateEventProcessingPage/ui/CreateEventCancel.vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import { eventApi, EventDB } from '@/enitites/event'
import { computed, inject, ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import { mdiLeadPencil, mdiMinusCircleOutline, mdiPlus, mdiUpload } from '@mdi/js'
import { EventCarpet } from '@/enitites/event/model/types'
import { CreateEventFormName } from '../model/types'

const { getImageById, saveImage } = useImage()
const props = withDefaults(
  defineProps<{
    isNew: boolean
    eventData: EventDB
    backStep: boolean
    screen: CreateEventFormName
  }>(),
  {
    isNew: true,
    backStep: false,
  }
)

const emit = defineEmits<{
  (e: 'updateEvent'): void
  (e: 'update:screen', value: CreateEventFormName): void
  (e: 'saveAsDraft'): void
}>()

const srcScoreboardBgImage = ref(
  props.eventData.scoreboardBgImage ? getImageById(props.eventData.scoreboardBgImage, 800, 450) : ''
)
const srcLogo = ref(props.eventData.scoreboardLogo ? getImageById(props.eventData.scoreboardLogo, 800, 800) : '')
const isShowImg = ref(props.backStep)
const filesLogoEvent = ref<FileList | null>(null)
const filesBgImageEvent = ref<FileList | null>(null)
const loadingNextBtn = ref(false)
const currentDotNav = ref(4)
const error = ref(null)
const inputBgImageRef = ref<HTMLInputElement | null>(null)
const inputLogoRef = ref<HTMLInputElement | null>(null)
const eventCarpets = ref<EventCarpet[]>(props.eventData.carpets || [])
const isUpdatingCarpets = ref(false)

const isInfoFilledOut = computed(() => !!props.eventData.title)

async function setBgImageToEvent() {
  if (!filesBgImageEvent.value?.[0]) return
  props.eventData.scoreboardBgImage = await saveImage(filesBgImageEvent.value[0])
  isShowImg.value = true
}

async function setLogoToEvent() {
  if (!filesLogoEvent.value?.[0]) return
  props.eventData.scoreboardLogo = await saveImage(filesLogoEvent.value[0])
  isShowImg.value = true
}

async function saveAsDraft() {
  if (filesBgImageEvent.value) await setBgImageToEvent()
  if (filesLogoEvent.value) await setLogoToEvent()

  emit('saveAsDraft')
  loadingNextBtn.value = false
}

async function handleAddCarpet() {
  const id = 0
  const newCarpetIndex = eventCarpets.value.length + 1
  eventCarpets.value.push({
    id,
    name: `Carpet ${newCarpetIndex}`,
  })
}

async function handleRemoveCarpet(index: number) {
  eventCarpets.value = eventCarpets.value.filter((_, i) => i !== index)
}

async function handleUpdateCarpetName(index: number, name: string) {
  eventCarpets.value[index].name = name
}

async function updateCarpets() {
  if (isUpdatingCarpets.value) return
  isUpdatingCarpets.value = true

  try {
    await eventApi.updateEventCarpets(props.eventData.id, eventCarpets.value)
  } catch (error) {
    console.error('Error updating carpets:', error)
  } finally {
    isUpdatingCarpets.value = false
  }
}

const isInitializingScoreboards = ref(false)
async function initScoreboards() {
  if (isInitializingScoreboards.value) return
  isInitializingScoreboards.value = true

  try {
    const data = await eventApi.initEventScoreboards(props.eventData.id)
    if (data) {
      console.log('Scoreboards initialized:', data)
    }
  } catch (error) {
    console.error('Error initializing scoreboards:', error)
  } finally {
    isInitializingScoreboards.value = false
  }
}

const injectedIsClickBtnNext = inject<{
  updateIsClickBtnNext: (value: boolean) => void
}>('isClickBtnNext')

async function handlerClickNext() {
  error.value = null
  loadingNextBtn.value = true

  if (filesBgImageEvent.value) await setBgImageToEvent()
  if (filesLogoEvent.value) await setLogoToEvent()
  if (eventCarpets.value.length) {
    await updateCarpets()
    await initScoreboards()
  }

  emit('updateEvent')
  loadingNextBtn.value = false
  injectedIsClickBtnNext?.updateIsClickBtnNext(true)

  emit('update:screen', props.backStep ? 'CreateEventTotal' : 'CreateEventBattles')
}

function openBgImageDialog() {
  inputBgImageRef.value?.click()
}

function handlerBgImageChange(evt: Event) {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  const reader = new FileReader()
  reader.onload = (event) => {
    srcScoreboardBgImage.value = event.target?.result as string
    filesBgImageEvent.value = filesImg
  }
  reader.readAsDataURL(filesImg[0])
}

function openLogoDialog() {
  inputLogoRef.value?.click()
}

function handlerLogoChange(evt: Event) {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  const reader = new FileReader()
  reader.onload = (event) => {
    srcLogo.value = event.target?.result as string
    filesLogoEvent.value = filesImg
  }
  reader.readAsDataURL(filesImg[0])
}

function handlerClickBack() {
  injectedIsClickBtnNext?.updateIsClickBtnNext(false)
  emit('update:screen', 'CreateEventTiers')
}

function handlerClickCancelEdit() {
  emit('update:screen', 'CreateEventTotal')
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';
@import '@/assets/style/navigationEventCreate';

.display-mode {
  max-width: 400px;
  margin: 140px auto 60px;

  .dm-section {
    margin-bottom: 40px;

    &__title {
      margin: 12px 0;
      color: var(--color-dark-800);
      font-weight: var(--font-weight-semi-bold);
    }

    &__description {
      color: #999;
    }
  }

  .carpet-item {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
  }

  .carpet__add-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: var(--font-weight-bold);
  }

  .dm-logo {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    height: auto;
    margin: 0 auto;
    object-fit: cover;
    object-position: center;
    background: #6cace4;
    aspect-ratio: 1 / 1;
  }

  .dm-bg-image {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: center;
    background: #6cace4;
    aspect-ratio: 16 / 9;
  }

  :deep(.v-responsive__content) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .btn-edit {
    position: absolute;
    bottom: 10px;
    left: 10px;
    font-weight: var(--font-weight-bold);
  }
}
</style>
